/**
 * Local-day arithmetic in one time zone. The app counts in the device's own zone; the server
 * counts in the zone a phone reports, so "today" and "late" mean the same on both sides.
 */
export interface Calendar {
  /** Local midnight of the day holding ts. */
  startOfDay(ts: number): number;
  /** The same wall-clock time a number of days later, across daylight-saving changes. */
  addDays(ts: number, days: number): number;
  /** The local date holding ts, as YYYY-MM-DD. */
  dayKey(ts: number): string;
  /** hh:mm on the local day holding ts. */
  at(ts: number, hh: number, mm: number): number;
}

export interface LocalDate {
  y: number;
  m: number;
  d: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Through Date, so a day stays a day across a DST change in the runtime's own zone. */
export const localCalendar: Calendar = {
  startOfDay(ts) {
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  },
  addDays(ts, days) {
    const d = new Date(ts);
    d.setDate(d.getDate() + days);
    return d.getTime();
  },
  dayKey(ts) {
    const d = new Date(ts);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  },
  at(ts, hh, mm) {
    const d = new Date(ts);
    d.setHours(hh, mm, 0, 0);
    return d.getTime();
  },
};

/** The wall clock of an instant in a zone. */
export function wallClock(tz: string, at: number): LocalDate & { hh: number; mm: number } {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  const parts: Record<string, string> = {};
  for (const p of f.formatToParts(new Date(at))) parts[p.type] = p.value;
  return { y: Number(parts['year']), m: Number(parts['month']), d: Number(parts['day']), hh: Number(parts['hour']), mm: Number(parts['minute']) };
}

function offsetAt(tz: string, at: number): number {
  const w = wallClock(tz, at);
  return Date.UTC(w.y, w.m - 1, w.d, w.hh, w.mm) - Math.floor(at / 60_000) * 60_000;
}

/** The instant of a local wall time in a zone; inside a DST gap the second guess lands after it. */
export function localInstant(tz: string, day: LocalDate, hh: number, mm: number): number {
  const guess = Date.UTC(day.y, day.m - 1, day.d, hh, mm);
  const first = guess - offsetAt(tz, guess);
  return guess - offsetAt(tz, first);
}

/** A zone the runtime knows, else null. */
export function knownZone(tz: string): string | null {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
}

/** Day arithmetic in a named IANA zone, whatever zone the runtime itself runs in. */
export function zoneCalendar(tz: string): Calendar {
  const shift = (day: LocalDate, days: number): LocalDate => {
    const t = new Date(Date.UTC(day.y, day.m - 1, day.d + days));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
  };
  return {
    startOfDay(ts) {
      return localInstant(tz, wallClock(tz, ts), 0, 0);
    },
    addDays(ts, days) {
      const w = wallClock(tz, ts);
      return localInstant(tz, shift(w, days), w.hh, w.mm);
    },
    dayKey(ts) {
      const w = wallClock(tz, ts);
      return `${w.y}-${pad(w.m)}-${pad(w.d)}`;
    },
    at(ts, hh, mm) {
      return localInstant(tz, wallClock(tz, ts), hh, mm);
    },
  };
}
